import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import { useQueries } from "react-query";
import '../scss/SurveyView.scss';
import {Link, NavLink, Redirect, Route, Switch, useParams, useRouteMatch, useHistory, Prompt, useLocation} from "react-router-dom";
import {
    GetCategories,
    GetCodes,
    GetContacts, GetDefaultErrorMessageWithSurveyLang,
    GetFollowUpConfig,
    GetFollowUpConfigWithLang,
    GetInstanceFollowupConfig, GetOrTextWithSurveyLang,
    GetSupportRoles,
    GetSurveyTypes,
    GetSurveyWithId, GetUseCases,
    GetUserBySurvey,
    GetUsers, UpdateSurveyWithId,
} from "../api/APISurvey";
import {FormattedMessage, useIntl} from "react-intl";
import {
    CampaignDropdown,
    getBranchIconColor,
    getMessageIcon,
    Modal,
    Popover, PopupElement, PopupProvider, ScrollableContainer,
    Skeleton,
    StatusText,
    Toast, Tooltip, UseCaseDropdown
} from "../UIElements";
import {ReactComponent as BackArrow} from "../svg/back.svg";
import DatePicker, {registerLocale} from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css";
import fi from "date-fns/locale/fi";
import {dateToServerFormat, dateToServerFormatWithZeroSeconds, formatDate, updateTimeToServerFormat} from "../Formats";
import SurveyHeader from "./SurveyHeader";
import SurveyMessages, {AddVariable, LengthIndicator, VariableComponent, variableStrategy} from "./SurveyMessages";
import {ReactComponent as LoaderIcon} from "../svg/loader.svg";
import {ReactComponent as ReminderIcon} from "../svg/reminder.svg";
import {ReactComponent as ReminderIconSmall} from "../svg/reminder-s.svg";
import {CompositeDecorator, ContentState, Editor, EditorState, Modifier} from "draft-js";
import {ReactComponent as OptionsIcon} from "../svg/options.svg";
import {ReactComponent as DeleteIcon} from "../svg/delete.svg";
import {ReactComponent as MessageIcon} from "../svg/message.svg";
import {ReactComponent as MinimizeIcon} from "../svg/minimize.svg";
import {ReactComponent as PathsIcon} from "../svg/paths.svg";
import _ from "lodash"
import {ReactComponent as SupportIcon} from "../svg/support.svg";
import  {SurveyCoordinators} from "./SurveySupportNeeds";
import SurveyRecipients from "./SurveyRecipients";
import SurveyResults from "./SurveyResults";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {useCustomerSegmentData, useLanguageData, useLanguageOptionsData} from "../App";
import {DataProvider, useDataContext} from "./SurveyContextProvider";
import {compareDateStrings, flattenConfigObject, reconstructConfigObject} from "../DataFunctions";
import {GetContactMeta, GetContactsAndRoles, GetUserMeta} from "../api/APIContact";
import {checkSurveyDataForBranchErrors, checkSurveyDataForErrors} from "../SurveyDataErrorChecks";
registerLocale("fi", fi)

const SurveyContext = React.createContext({
    surveyContext: {
        config: {},
        contacts: null,
        endtime: "",
        id: "",
        starttime: "",
        status: "DRAFT",
        updated: "",
        updatedby: ""
    },
    setSurveyContext: () => {}
})

const OriginalSurveyContext = React.createContext({
    originalSurveyContext: {},
    setOriginalSurveyContext: () => {}
})

const UserContext = React.createContext({
    userContext: {},
    setUserContext: () => {}
})

const UserSurveyContext = React.createContext({
    userSurveyContext: {},
    setUserSurveyContext: () => {}
})

const OriginalUserSurveyContext = React.createContext({
    originalUserSurveyContext: {},
    setOriginalUserSurveyContext: () => {}
})

const FollowUpConfigContext = React.createContext({
    followUpConfigContext: {},
    setFollowUpConfigContext: () => {}
})

const SupportRolesContext = React.createContext({
    supportRolesContext: {},
    setSupportRolesContext: () => {}
})

const CodesContext = React.createContext({
    codesContext: {},
    setCodesContext: () => {}
})

const ContactsContext = React.createContext({
    contactsContext: {},
    setContactsContext: () => {}
})

const CopiedUsersContext = React.createContext({
    copiedUsersContext: {},
    setCopiedUsersContext: () => {}
})

const newSurveyData = {
    id: "new",
    updated: "",
    updatedby: "",
    starttime: dateToServerFormatWithZeroSeconds(new Date()),
    endtime: dateToServerFormatWithZeroSeconds(new Date()),
    config: {
        campaign: "",
        title: "",
        message: ""
    },
    status: "DRAFT",
    contacts: []
}
const branchOptionErrorDefaultObject = {
    "missingChildErrors": [],
    "missingOptionsErrors": []
}

export const EditorFocusContext = React.createContext({
    editors: {},
    anyEditorFocused: false,
    setFocus: (id, state) => {}
})
function EditorFocusProvider({children}) {
    const [editorFocusStates, setEditorFocusStates] = useState({})

    const setFocus = (id, state) => {
        setEditorFocusStates(prev => ({
            ...prev,
            [id]: state,
        }));
    }

    const anyEditorFocused = Object.values(editorFocusStates).some(state => state)

    return (
        <EditorFocusContext.Provider
            value={{
                editors: editorFocusStates,
                anyEditorFocused: anyEditorFocused,
                setFocus: setFocus
            }}>
            {children}
        </EditorFocusContext.Provider>
    );
}

export default function SurveyView() {
    let { surveyId } = useParams()

    return <EditorFocusProvider>
        <DataProvider key={surveyId} surveyId={surveyId}>
        <SurveyComponent key={surveyId} surveyId={surveyId}/>
    </DataProvider>
    </EditorFocusProvider>
}

function SurveyComponent({ surveyId }) {
    const intl = useIntl()

    const [statusCheck, setStatusCheck] = useState(false)
    const [showNewVersionModal, setShowNewVersionModal] = useState(false)
    const [viewingNewVersion, setViewingNewVersion] = useState(false)
    const [contentVersion, setContentVersion] = useState(0)
    const [newerVersionDismissed, setNewerVersionDismissed] = useState(false)

    // Contexts
    const {customerSegment} = useCustomerSegmentData()
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: userSurveyData, setValue: setUserSurveyData } = useDataContext('userSurveyData')
    const { value: originalSurveyData, setValue: setOriginalSurveyData } = useDataContext('originalSurveyData')
    const { value: originalUserSurveyData, setValue: setOriginalUserSurveyData } = useDataContext('originalUserSurveyData')
    const { value: userData, setValue: setUserData } = useDataContext('userData')
    const { value: categoryData, setValue: setCategoryData } = useDataContext('categoryData')
    const { value: contactsData, setValue: setContactsData } = useDataContext('contactsData')
    const { value: followUpConfigData, setValue: setFollowUpConfigData } = useDataContext('followUpConfigData')
    const { value: supportRolesData, setValue: setSupportRolesData } = useDataContext('supportRolesData')
    const { value: copiedUsersData, setValue: setCopiedUsersData } = useDataContext('copiedUsersData')
    const { value: oldSurveyData, setValue: setOldSurveyData } = useDataContext('oldSurveyData')
    const { value: oldUserSurveyData, setValue: setOldUserSurveyData } = useDataContext('oldUserSurveyData')
    const { value: contactMeta, setValue: setContactMeta } = useDataContext('contactMeta')
    const { value: contactMetaArray, setValue: setContactMetaArray} = useDataContext('contactMetaArray')
    const { value: contactsAndRoles, setValue: setContactsAndRoles} = useDataContext('contactsAndRoles')
    const { value: userMeta, setValue: setUserMeta } = useDataContext('userMeta')
    const { value: surveyTypes, setValue: setSurveyTypes } = useDataContext('surveyTypes')
    const { value: branchOptionErrors, setValue: setBranchOptionErrors } = useDataContext('branchOptionErrors')
    const { value: useCases, setValue: setUseCases } = useDataContext('useCases')

    // Queries
    const getSurveyData = GetSurveyWithId(surveyId)
    const getUserSurveyData = GetUserBySurvey(surveyId)
    const getUserData = GetUsers()
    const getCategoryData = GetCategories()
    const getContactsData = GetContacts()
    const getFollowUpConfigData = GetInstanceFollowupConfig()
    const getSupportRolesData = GetSupportRoles()
    const getContactMeta = GetContactMeta()
    const getContactsAndRoles = GetContactsAndRoles()
    const getUserMeta = GetUserMeta()
    const getSurveyTypes = GetSurveyTypes()
    const getUseCases = GetUseCases()

    const queries = [
        getSurveyData,
        getUserSurveyData,
        getUserData,
        getCategoryData,
        getContactsData,
        getFollowUpConfigData,
        getSupportRolesData,
        getContactMeta,
        getContactsAndRoles,
        getUserMeta,
        getSurveyTypes,
        getUseCases
    ]

    // If all queries are successful set state and contexts
    useEffect(() => {
        const queriesLoaded = queries.every((query) => query.isSuccess)

        if (queriesLoaded) {
            const dataOfSurvey = surveyId === "new" ? newSurveyData : getSurveyData.data[0]
            const surveyDataCopy = _.cloneDeep(dataOfSurvey)
            const flattenedSurvey = flattenConfigObject(surveyDataCopy)
            const contactMetaData = getContactMeta?.data[0]
            const surveyTypeData = getSurveyTypes?.data[0]
            const sortedContactMeta = Object.keys(contactMetaData).sort((a, b) => contactMetaData[a]?.order - contactMetaData[b]?.order)
            const useCasesData = getUseCases?.data[0]

            const filterUseCasesBySegment = (useCases, customerSegment) => {
                if (!customerSegment) {
                    return useCases
                }
                // Filter use cases by customerSegment + include use cases without customersegment value like custom use cases
                return Object.entries(useCases).reduce((filtered, [key, useCase]) => {
                    if (!('customersegment' in useCase) || useCase.customersegment === customerSegment) {
                        filtered[key] = useCase
                    }
                    return filtered
                }, {})
            }

            const filteredUseCases = filterUseCasesBySegment(useCasesData, customerSegment)

            // Only update surveyData and userSurveyData if they are not already loaded from local storage
            if (!surveyData) {
                setSurveyData(flattenedSurvey);
            }
            if (!userSurveyData) {
                setUserSurveyData(getUserSurveyData.data);
            }
            setOriginalSurveyData(flattenedSurvey);
            setOriginalUserSurveyData(getUserSurveyData.data);
            setUserData(getUserData.data)
            setCategoryData(getCategoryData.data[0])
            setContactsData(getContactsData.data)
            setFollowUpConfigData(getFollowUpConfigData.data)
            setSupportRolesData(Object.keys(getSupportRolesData.data[0]))
            setCopiedUsersData([])
            setContactMeta(contactMetaData)
            setContactMetaArray(sortedContactMeta)
            setContactsAndRoles(getContactsAndRoles?.data)
            setUserMeta(getUserMeta?.data[0])
            setSurveyTypes(surveyTypeData)
            setBranchOptionErrors(branchOptionErrorDefaultObject)
            setUseCases(filteredUseCases)

            let fetchedDataIsNewer = false
            try {
                fetchedDataIsNewer =  surveyId !== "new" && compareDateStrings(dataOfSurvey?.updated, surveyData?.updated) > 0;
            } catch (error) {
                console.error(error.message);
            }

            if (fetchedDataIsNewer) {
                setShowNewVersionModal(true)
            }
        }
        setStatusCheck(queriesLoaded)
    }, [...queries.map((query) => query.isSuccess)])

    // Check if server has newer data
    useEffect(()=>{
        if (surveyId !== "new" && getSurveyData.isSuccess && !newerVersionDismissed) {
            const dataOfSurvey = getSurveyData?.data[0]
            let fetchedDataIsNewer = false
            try {
                fetchedDataIsNewer =  surveyId !== "new" && compareDateStrings(dataOfSurvey?.updated, surveyData?.updated) > 0;
            } catch (error) {
                console.error(error.message);
            }

            if (fetchedDataIsNewer) {
                setShowNewVersionModal(true)
            } else {
                if (!viewingNewVersion) {
                    setShowNewVersionModal(false)
                }
            }
        }
    },[getSurveyData])

    const viewNeverVersionOfSurvey = () => {
        setViewingNewVersion(true)
        setOldSurveyData(surveyData)
        setOldUserSurveyData(userSurveyData)
        setSurveyData(flattenConfigObject(getSurveyData.data[0]))
        setUserSurveyData(getUserSurveyData.data)
        setContentVersion(prevContentVersion => prevContentVersion + 1)
    }

    const revertToOlderVersion = () => {
        setViewingNewVersion(false)
        setSurveyData(oldSurveyData)
        setUserSurveyData(oldUserSurveyData)
        setContentVersion(prevContentVersion => prevContentVersion - 1)
    }

    return <>
        <div className={"survey-container"}>
            {
                !statusCheck ? <SurveySkeleton /> :
                    getSurveyData.isError ? <span>Error: {getSurveyData.error.message}</span> :
                        <>
                                <SurveyEditorContent contentVersion={contentVersion}/>
                        </>
            }
        </div>
        {showNewVersionModal &&
            <div className={"snackbar"}>
                {viewingNewVersion ?
                    <>
                        <p>
                            {intl.formatMessage({
                                id: "viewingNewerVersionOfSurvey",
                                defaultMessage: "Viewing the newer version of this survey"
                            })}
                        </p>
                        <button className={"primary"} onClick={() => {
                            setShowNewVersionModal(false)
                            setViewingNewVersion(false)
                        }}>
                            {intl.formatMessage({
                                id: "selectVersion",
                                defaultMessage: "Select version"
                            })}
                        </button>
                        <button className={"secondary"} onClick={()=> revertToOlderVersion()}>
                            {intl.formatMessage({
                                id: "revert",
                                defaultMessage: "Revert"
                            })}
                        </button>
                    </> :
                    <>
                        <p>
                            {intl.formatMessage({id:"newerVersionOfSurveyAvailable", defaultMessage:"There is a newer version of this survey available"})}
                        </p>
                        <button className={"primary"} onClick={() => viewNeverVersionOfSurvey()}>
                            {intl.formatMessage({
                                id: "viewVersion",
                                defaultMessage: "View Version"
                            })}
                        </button>
                        <button className={"secondary"} onClick={()=> {
                            setShowNewVersionModal(false)
                            setNewerVersionDismissed(true)
                            setViewingNewVersion(false)
                        }}>
                            {intl.formatMessage({
                                id: "dismiss",
                                defaultMessage: "Dismiss"
                            })}
                        </button>
                    </>
                }

            </div>
        }
    </>
}

function SurveyEditorContent({contentVersion}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { value: branchOptionErrors, setValue: setBranchOptionErrors } = useDataContext('branchOptionErrors')

    useEffect(()=>{
        // Check for errors
        const branchErrors = checkSurveyDataForBranchErrors(surveyData)
        setBranchOptionErrors(branchErrors)
    },[surveyData])

    useOtherBranchUpdater(surveyData, setSurveyData)

    return <>
        <SurveyHeader />
        <SurveyStatusBanner />
        <div className={"survey-view"}>
            <div className={"survey-content"}>
                <SurveyNavigation />
                <SurveyContent key={contentVersion}/>
            </div>
        </div>
    </>
}
function useOtherBranchUpdater(surveyData, setSurveyData) {
    const intl = useIntl()
    const { instanceLang } = useLanguageData()
    const surveyLanguage = surveyData?.config?.language || instanceLang

    const defaultOrText = intl.formatMessage({ defaultMessage: 'or', id: 'or' })
    const defaultErrorMessage = intl.formatMessage({
        defaultMessage: 'Sorry, I didn\'t understand. Please reply with {options}.',
        id: 'defaultErrorMessage'
    })

    const [orText, setOrText] = useState(defaultOrText)
    const [errorMessage, setErrorMessage] = useState(defaultErrorMessage)

    const getErrorMessage = GetDefaultErrorMessageWithSurveyLang(surveyLanguage)
    const getOrText = GetOrTextWithSurveyLang(surveyLanguage)

    useEffect(() => {
        if (getOrText.isSuccess) {
            setOrText(getOrText.data[0]?.value || defaultOrText)
        }
    }, [getOrText.data, getOrText.isSuccess])

    useEffect(() => {
        if (getErrorMessage.isSuccess) {
            setErrorMessage(getErrorMessage.data[0]?.value || defaultErrorMessage)
        }
    }, [getErrorMessage.data, getErrorMessage.isSuccess])

    useEffect(() => {
        const updatedConfig = updateOtherMessages(surveyData.config, orText, errorMessage)

        if (JSON.stringify(surveyData.config) !== JSON.stringify(updatedConfig)) {
            setSurveyData({ ...surveyData, config: updatedConfig }, false)
        }
    }, [surveyData, orText, errorMessage, surveyLanguage])
}

function updateOtherMessages(data, orText, defaultErrorMessage) {
    const extractOption = (condition) => {
        const match = condition.match(/\^(.*?)\\b/)
        return match ? match[1] : ''
    }

    // Creating a deep copy of data to avoid direct mutation
    let newData = JSON.parse(JSON.stringify(data));

    // Iterate over each key in the newData object
    for (const key in newData) {
        // Check if the key starts with 'other'
        if (key.startsWith('other')) {
            const parentKey = newData[key].parent
            const options = []

            // Find all corresponding branch keys
            for (const branchKey in newData) {
                if (branchKey.startsWith('branch') && newData[branchKey].parent === parentKey) {
                    // Extract option from the branch condition
                    const option = extractOption(newData[branchKey].condition);
                    if (option) {
                        options.push(option.toUpperCase())
                    }
                }
            }

            // Update the message of the other key
            if (options.length > 0) {
                let optionsText
                if (options.length > 1) {
                    options.sort()
                    const lastOption = options.pop();

                    optionsText = `${options.join(', ')} ${orText} ${lastOption}`
                } else {
                    optionsText = options[0]
                }

                newData[key].message = defaultErrorMessage.replace('{options}', optionsText)
            }
        }
    }

    return newData
}

function SurveyProvider({children, surveyContext}) {
    const [surveyData, setSurveyData] = useState(surveyContext)
    const value = useMemo(() =>(
        {surveyData, setSurveyData}
    ), [surveyData])
    useEffect(()=>{
        let surveyDataWithUpdateTime = {...surveyData}
        surveyDataWithUpdateTime.updated = dateToServerFormat(new Date())
        sessionStorage.setItem("stored-"+surveyData.id, JSON.stringify(surveyDataWithUpdateTime))
    },[surveyData])
    return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
}

export function useSurveyData() {
    const context = useContext(SurveyContext)
    if (context === undefined) {
        throw new Error('useSurveyData must be used within a SurveyProvider')
    }
    return context
}

function OriginalSurveyProvider({children, originalSurveyContext}) {
    const [originalSurveyData, setOriginalSurveyData] = useState(originalSurveyContext)
    const value = useMemo(() =>(
        {originalSurveyData, setOriginalSurveyData}
    ), [originalSurveyData])
    return <OriginalSurveyContext.Provider value={value}>{children}</OriginalSurveyContext.Provider>
}

export function useOriginalSurveyData() {
    const context = useContext(OriginalSurveyContext)
    if (context === undefined) {
        throw new Error('useOriginalSurveyData must be used within a OriginalSurveyProvider')
    }
    return context
}

function UserProvider({children, userContext, surveyId}) {
    const [userData, setUserData] = useState(userContext)
    const value = useMemo(() =>(
        {userData, setUserData}
    ), [userData])
    return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

export function useUserData() {
    const context = useContext(UserContext)
    if (context === undefined) {
        throw new Error('useUserData must be used within a UserProvider')
    }
    return context
}

function UserSurveyProvider({children, userSurveyContext, surveyId}) {
    const [userSurveyData, setUserSurveyData] = useState(userSurveyContext)
    const value = useMemo(() =>(
        {userSurveyData, setUserSurveyData}
    ), [userSurveyData])
    useEffect(()=>{
        sessionStorage.setItem("userSurveyData-"+surveyId, JSON.stringify(userSurveyData))
    },[userSurveyData, surveyId])
    return <UserSurveyContext.Provider value={value}>{children}</UserSurveyContext.Provider>
}

export function useUserSurveyData() {
    const context = useContext(UserSurveyContext)
    if (context === undefined) {
        throw new Error('useUserSurveyData must be used within a UserSurveyProvider')
    }
    return context
}

function OriginalUserSurveyProvider({children, originalUserSurveyContext}) {
    const [originalUserSurveyData, setOriginalUserSurveyData] = useState(originalUserSurveyContext)
    const value = useMemo(() =>(
        {originalUserSurveyData, setOriginalUserSurveyData}
    ), [originalUserSurveyData])
    return <OriginalUserSurveyContext.Provider value={value}>{children}</OriginalUserSurveyContext.Provider>
}

export function useOriginalUserSurveyData() {
    const context = useContext(OriginalUserSurveyContext)
    if (context === undefined) {
        throw new Error('useOriginalUserSurveyData must be used within a OriginalUserSurveyProvider')
    }
    return context
}

function FollowUpConfigProvider({children, configContext}) {
    const [followUpConfigData, setFollowUpConfigData] = useState(configContext)
    const value = useMemo(()=>(
        {followUpConfigData, setFollowUpConfigData}
    ), [followUpConfigData])
    return <FollowUpConfigContext.Provider value={value}>{children}</FollowUpConfigContext.Provider>
}

export function useFollowUpConfigData(){
    const context = useContext(FollowUpConfigContext)
    if (context === undefined) {
        throw new Error('useFollowUpConfigData must be used within a FollowUpConfigProvider')
    }
    return context
}

function SupportRolesProvider({children, supportRolesContext}) {
    const [supportRolesData, setSupportRolesData] = useState(supportRolesContext)
    const value = useMemo(()=>(
        {supportRolesData, setSupportRolesData}
    ), [supportRolesData])
    return <SupportRolesContext.Provider value={value}>{children}</SupportRolesContext.Provider>
}

export function useSupportRolesData(){
    const context = useContext(SupportRolesContext)
    if (context === undefined) {
        throw new Error('useSupportRolesData must be used within a SupportRolesProvider')
    }
    return context
}

function CodesProvider({children, codesContext}) {
    const [codesData, setCodesData] = useState(codesContext)
    const value = useMemo(() =>(
        {codesData, setCodesData}
    ), [codesData])
    return <CodesContext.Provider value={value}>{children}</CodesContext.Provider>
}

export function useCodesData() {
    const context = useContext(CodesContext)
    if (context === undefined) {
        throw new Error('useCodesData must be used within a CodesProvider')
    }
    return context
}

function ContactsProvider({children, contactsContext}) {
    const [contactsData, setContactsData] = useState(contactsContext)
    const value = useMemo(() =>(
        {contactsData, setContactsData}
    ), [contactsData])
    return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>
}

export function useContactsData() {
    const context = useContext(ContactsContext)
    if (context === undefined) {
        throw new Error('useContactsData must be used within a ContactsProvider')
    }
    return context
}

function CopiedUsersProvider({children}) {
    const [copiedUsersData, setCopiedUsersData] = useState([])
    const value = useMemo(() =>(
        {copiedUsersData, setCopiedUsersData}
    ), [copiedUsersData])
    return <CopiedUsersContext.Provider value={value}>{children}</CopiedUsersContext.Provider>
}

export function useCopiedUsersData() {
    const context = useContext(CopiedUsersContext)
    if (context === undefined) {
        throw new Error('useCopiedUsersData must be used within a CopiedUsersProvider')
    }
    return context
}

function ContextOfSurvey({surveyId, surveyContext, originalData, userContext, userSurveyContext, codesContext, originalUserSurveyData, contactsContext, followUpConfigContext, supportRolesContext}) {
    const supportRoles = Object.keys(supportRolesContext)
    return <SurveyProvider surveyContext={surveyContext}>
        <UserProvider userContext={userContext} surveyId={surveyId}>
            <UserSurveyProvider userSurveyContext={userSurveyContext} surveyId={surveyId}>
                <CodesProvider codesContext={codesContext}>
                    <ContactsProvider contactsContext={contactsContext}>
                        <OriginalSurveyProvider originalSurveyContext={originalData}>
                            <OriginalUserSurveyProvider originalUserSurveyContext={originalUserSurveyData}>
                                <FollowUpConfigProvider configContext={followUpConfigContext}>
                                    <SupportRolesProvider supportRolesContext={supportRoles}>
                                        <CopiedUsersProvider>
                                            <SurveyHeader />
                                            <SurveyStatusBanner />
                                            <div className={"survey-view"}>
                                                <div className={"survey-content"}>
                                                    <SurveyNavigation />
                                                    <SurveyContent />
                                                </div>
                                            </div>
                                        </CopiedUsersProvider>
                                    </SupportRolesProvider>
                                </FollowUpConfigProvider>
                            </OriginalUserSurveyProvider>
                        </OriginalSurveyProvider>
                    </ContactsProvider>
                </CodesProvider>
            </UserSurveyProvider>
        </UserProvider>
    </SurveyProvider>
}

function SurveyStatusBanner() {
    const { value: surveyData } = useDataContext('surveyData')
    const intl = useIntl()
    const [status, setStatus] = useState(surveyData.status)
    const [showBanner, setShowBanner] = useState(true)
    let statusText
    let statusShowBanner = status === "IN PROGRESS" || status === "SCHEDULED" || status === "FINISHED" || status === "ARCHIVED" || status === "DELETED"
    let renderBanner = showBanner && statusShowBanner
    let statusClass = "survey-status-banner"

    if (status === "IN PROGRESS") {
        statusClass = statusClass.concat(" in-progress")
        statusText = intl.formatMessage(
            {
                id: 'survey.statusBanner.inProgress',
                defaultMessage: '🚨 The survey is in progress! Editing is limited.',
            })
    }
    if (status === "SCHEDULED") {
        statusClass = statusClass.concat(" scheduled")
        statusText = intl.formatMessage(
            {
                id: 'survey.statusBanner.scheduled',
                defaultMessage: '⏱ This survey is scheduled to start. If you wish to make changes switch the survey to Edit-mode, and republish after you are done.',
            })
    }
    if (status === "FINISHED") {
        statusClass = statusClass.concat(" finished")
        statusText = intl.formatMessage(
            {
                id: 'survey.statusBanner.finished',
                defaultMessage: '🎉 This survey is finished. If you wish to edit it consider creating a new survey by duplicating this one.',
            })
    }
    if (status === "ARCHIVED") {
        statusClass = statusClass.concat(" archived")
        statusText = intl.formatMessage(
            {
                id: 'survey.statusBanner.archived',
                defaultMessage: '🗃 This survey has been archived. Editing is limited.',
            })
    }
    if (status === "DELETED") {
        statusClass = statusClass.concat(" archived")
        statusText = intl.formatMessage(
            {
                id: 'survey.statusBanner.deleted',
                defaultMessage: '🗑 This survey has been deleted.',
            })
    }

    return renderBanner &&
        <div className={statusClass}>
            {status === "SCHEDULED" ?
                <>
                <div className={"count-container"}>
                    <CountDownTimer />
                    <div className={"close-toggle-container"}>
                        <div className={"close-toggle"} onClick={()=>setShowBanner(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>
                    {statusText}
                </> :
                <>
                    {statusText}
                    <div className={"close-toggle-container"}>
                        <div className={"close-toggle"} onClick={()=>setShowBanner(false)}>
                            <CloseIcon />
                        </div>
                    </div>
                </>
            }
    </div>
}

function CountDownTimer() {
    const { value: surveyData } = useDataContext('surveyData')
    const intl = useIntl()

    const calculateTimeLeft = () => {
        let countTo = new Date(surveyData.starttime).getTime()
        let countFrom = new Date().getTime()
        let countRemainder = countTo - countFrom
        let timeLeft = {}
        if (countRemainder > 0) {
            timeLeft = {
                days : Math.floor(countRemainder / (1000 * 60 * 60 * 24)),
                hours : Math.floor((countRemainder % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes : Math.floor((countRemainder % (1000 * 60 * 60)) / (1000 * 60)),
                seconds : Math.floor((countRemainder % (1000 * 60)) / 1000)
            }
        } else {
            timeLeft = null
        }
        return timeLeft
    }

    const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())

    useEffect(()=> {
        const timer = setTimeout(()=>{
            setTimeLeft(calculateTimeLeft())
        }, 1000)
        return () => clearTimeout(timer)
    })

    return <div className={"count-down-timer"}>
        ⏱ {timeLeft ?
            <>{timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s</> :
            intl.formatMessage(
                {
                    id: 'survey.startingSurvey',
                    defaultMessage: 'Starting Survey...',
                })
        }
    </div>
}

function SurveyPreview() {
    const { value: surveyData } = useDataContext('surveyData')
    const intl = useIntl()
    const [previewOpen, setPreviewOpen] = useState( localStorage.getItem("previewOpen") ? JSON.parse(localStorage.getItem("previewOpen")) : true)
    return <div className={previewOpen ? "survey-preview" : "survey-preview closed"}>
        <div className={"preview-header"}>
            {previewOpen &&
            <h3>
                {intl.formatMessage(
                    {
                        id: 'survey.preview.preview',
                        defaultMessage: 'Preview',
                    })}
            </h3>
            }
            {previewOpen ?
                <button onClick={() => {
                    localStorage.setItem("previewOpen", JSON.stringify(!previewOpen))
                    setPreviewOpen(!previewOpen)
                }}>
                    <MinimizeIcon/>
                </button> :
                <button onClick={() => {
                    localStorage.setItem("previewOpen", JSON.stringify(!previewOpen))
                    setPreviewOpen(!previewOpen)
                }}>
                    <PathsIcon/>
                </button>
            }
        </div>
        {previewOpen &&
        <div className={"messages"}>
            <div className={"message-container has-steps"}>
                <div className={"message"}>
                    <div className={"message-icon"} title={surveyData.config.message}>
                        <MessageIcon/>
                    </div>
                </div>
                <div className={"message-children"}>
                    <GetPreviewChildren data={surveyData.config} depth={1}/>
                </div>
            </div>
        </div>
        }
    </div>
}

function GetPreviewChildren(props) {
    return <>
        {Object.keys(props.data).filter(key => key.startsWith('branch') || key === 'other').sort()
            .map( (keyValue, i) => {
                return <PreviewChild data={props.data} parentColor={props.parentColor} keyValue={keyValue} i={i} key={i} depth={props.depth} parent={props.parent} parentsParent={props.parentsParent} parentsParentsParent={props.parentsParentsParent}/>
            })}
    </>
}

function PreviewChild(props) {
    let isBranch = props.keyValue.startsWith('branch')
    let icon = getMessageIcon(props, isBranch)
    let color = getBranchIconColor(props, isBranch)

    return <div className={isBranch ? "message-container has-steps" : "message-container"} key={props.i}>
        <div className={"message"}>
            <div className={"message-icon"} title={props.data[props.keyValue].message} style={{backgroundColor:color}}>{icon}</div>
            {props.data[props.keyValue].hasOwnProperty("supportneed") &&
            props.data[props.keyValue]["supportneed"] &&
            <SupportIcon/>
            }
        </div>
            {
                isBranch &&
                <>
                    <div className={"message-children"}>
                        <GetPreviewChildren data={props.data[props.keyValue]} parentColor={color} depth={props.depth + 1} parent={props.keyValue} parentsParent={props.parent} parentsParentsParent={props.parentsParent}/>
                    </div>
                </>
            }
    </div>
}

function SurveyNavigation() {
    const intl = useIntl()
    let { url } = useRouteMatch()

    return <div className={"survey-navigation"}>
        <nav>
            <ul>
                <li>
                    <NavLink to={`${url}/information`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.information',
                                defaultMessage: 'Information',
                            })}
                    </NavLink>
                </li>
                <li>
                    <NavLink to={`${url}/recipients`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.recipients',
                                defaultMessage: 'Recipients',
                            })}
                    </NavLink>
                </li>
                <li>
                    <NavLink to={`${url}/messages`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.messages',
                                defaultMessage: 'Messages',
                            })}
                    </NavLink>
                </li>
                <li>
                    <NavLink to={`${url}/reminders`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.reminders',
                                defaultMessage: 'Reminders',
                            })}
                    </NavLink>
                </li>
                {
                    /*
                    <li>
                    <NavLink to={`${url}/support-needs`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.supportNeeds',
                                defaultMessage: 'Support Needs',
                            })}
                    </NavLink>
                </li>
                     */
                }
                <li>
                    <NavLink to={`${url}/results`}>
                        {intl.formatMessage(
                            {
                                id: 'survey.navigation.results',
                                defaultMessage: 'Results',
                            })}
                    </NavLink>
                </li>
            </ul>
        </nav>
    </div>
}

function SurveyContent() {
    let { url, path } = useRouteMatch()

    return <>
        <Switch>
            <Route path={`${path}/information`}>
                <div className={`survey-edit`}>
                <SurveyInformation />
                </div>
            </Route>
            <Route path={`${path}/messages`}>
                <ScrollableContainer className={"survey-edit"}>
                <SurveyMessages />
                </ScrollableContainer>
            </Route>
            <Route path={`${path}/reminders`}>
                <div className={`survey-edit`}>
                <SurveyReminders />
                </div>
            </Route>
            {
                /*
                <Route path={`${path}/support-needs`}>
                <SurveySupportNeeds />
            </Route>
                 */
            }
            <Route path={`${path}/recipients`}>
                <div className={`survey-edit`}>
                <SurveyRecipients />
                </div>
            </Route>
            <Route path={`${path}/results`}>
                <div className={`survey-edit`}>
                <SurveyResults />
                </div>
            </Route>
            <Route path={path}>
                <Redirect to={`${url}/information`} />
            </Route>
        </Switch>
    </>
}

function SurveyInformation() {
    const intl = useIntl()
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const surveyName = surveyData.config.title
    const campaignName = surveyData?.config?.campaign || ''
    const surveyType = surveyData?.config?.type || ''
    const surveyUseCase = surveyData?.config?.usecase || ''
    const startDate = new Date(formatDate(surveyData.starttime))
    const endDate = new Date(formatDate(surveyData.endtime))
    let datepickerPlaceholder = intl.formatMessage(
            {
                id: 'survey.information.datepickerPlaceholder',
                defaultMessage: 'Click to select date',
            })
    let dateFormat
    let locale
    let timeFormat
    let timeCaption
    switch (intl.locale) {
        case "en":
            dateFormat = "MMMM d, yyyy, h:mm aa"
            timeFormat = "h:mm aa"
            timeCaption = "Time"
            locale = ""
            break
        case "fi":
            dateFormat = "d. MMMM yyyy, HH:mm"
            timeFormat = "HH:mm"
            timeCaption = "Aika"
            locale = "fi"
            break
        default:
            dateFormat = "MMMM d, yyyy, h:mm aa"
            timeFormat = "h:mm aa"
            timeCaption = "Time"
            locale = ""
    }

    useEffect(()=>{
        if (startDate >= endDate) {
            const newEndDate = new Date(startDate)
            newEndDate.setDate(newEndDate.getDate() + 7) // Week from start date

            const newSurveyData = {...surveyData}
            newSurveyData.endtime = dateToServerFormatWithZeroSeconds(newEndDate)
            setSurveyData(newSurveyData)
        }
    },[startDate])

    useEffect(()=>{
        if (startDate.getSeconds() !== 0) {
            const newStartDate = new Date(startDate)
            const newSurveyData = {...surveyData}
            newSurveyData.starttime = dateToServerFormatWithZeroSeconds(newStartDate)
            setSurveyData(newSurveyData)
        }
        if (endDate.getSeconds() !== 0) {
            const newEndDate = new Date(endDate)
            const newSurveyData = {...surveyData}
            newSurveyData.endtime = dateToServerFormatWithZeroSeconds(newEndDate)
            setSurveyData(newSurveyData)
        }
    }, [])

    const handleSurveyDataChange = (data, e) => {
        const newValue = e?.target ? e.target.value : e
        const newSurveyData = {...surveyData}
        newSurveyData.config[data] = newValue
        setSurveyData(newSurveyData)
    }

    const {instanceLang} = useLanguageData()
    const surveyHasLanguageParameter = surveyData?.config.hasOwnProperty("language") && surveyData?.config?.language
    const surveyLanguage = surveyHasLanguageParameter ? surveyData?.config?.language : instanceLang
    const {instanceLangOptions} = useLanguageOptionsData()
    const instanceHasMultipleLanguageOptions = instanceLangOptions && instanceLangOptions?.length > 1

    const currentDate = new Date()
    const minDate = startDate && startDate < currentDate ? startDate : currentDate
    const maxDate = new Date(currentDate.getFullYear() + 2, currentDate.getMonth(), currentDate.getDate())

    const { value: surveyTypes } = useDataContext('surveyTypes')
    const sortedSurveyTypeArray = Object.keys(surveyTypes)
        .filter(key => surveyTypes[key].hasOwnProperty("adminVisible") && surveyTypes[key]['adminVisible'])
        .sort((a,b) => surveyTypes[a]?.order - surveyTypes[b]?.order)

    const { value: useCases } = useDataContext('useCases')

    return <PopupProvider>
        <h4>
            {intl.formatMessage(
                {
                    id: 'survey.navigation.information',
                    defaultMessage: 'Information',
                })}
        </h4>
        <label htmlFor={"survey-name"}>
            {intl.formatMessage(
                {
                    id: 'survey.information.name',
                    defaultMessage: 'Name',
                })}
        </label>
        <input type={"text"} id={"survey-name"}
               value={surveyData?.config?.title || ""}
               onChange={e => {
                   const newSurveyData = {...surveyData}
                   newSurveyData.config.title = e.target.value
                   setSurveyData(newSurveyData)
               }}
               placeholder={intl.formatMessage(
                       {
                           id: 'survey.information.name.placeholder',
                           defaultMessage: 'Give name for survey',
                       })}/>
        <div className={"grid"}>
            <div className={"survey-dropdown"}>
                <label className={"flex-header"}>
                    {intl.formatMessage(
                        {
                            id: 'assignToCampaign',
                            defaultMessage: 'Assign to Campaign',
                        })}
                    <Tooltip text={intl.formatMessage({id:"assignToCampaignTooltip", defaultMessage:"Include this survey as part of your chosen campaign"})} />
                </label>
                <CampaignDropdown
                    surveyData={surveyData}
                    setSurveyData={setSurveyData}
                    campaignName={campaignName}
                />
            </div>
            <div className={"survey-dropdown"}>
                <label className={"flex-header"}>
                    {intl.formatMessage(
                        {
                            id: 'assignUseCase',
                            defaultMessage: 'Assign Use Case',
                        })}
                    <Tooltip className="pre" text={intl.formatMessage({id:"usecases.tooltip", defaultMessage:"Assign a use case to the survey to track and view data by use case across multiple surveys."})} />
                </label>
                <UseCaseDropdown
                    setValue={(value) => handleSurveyDataChange("usecase", value)}
                    value={surveyUseCase}
                />
            </div>
        </div>
        <div className={"survey-time"}>
            <div>
                <label htmlFor={"survey-start"}>
                    {intl.formatMessage(
                        {
                            id: 'survey.information.startTime',
                            defaultMessage: 'Start Time',
                        })}
                </label>
                {limitedEditing ?
                    <DatePicker id={"survey-start"}
                                selectsStart startDate={startDate} endDate={endDate}
                                showTimeSelect timeIntervals={15} timeFormat={timeFormat} timeCaption={timeCaption}
                                selected={startDate}
                                dateFormat={dateFormat} locale={locale}
                                placeholderText={datepickerPlaceholder}
                                showMonthYearDropdown dateFormatCalendar={"MMM yyyy"} minDate={minDate} maxDate={maxDate} todayButton={intl.formatMessage({defaultMessage:'Today',id:'today'})}
                                readOnly disabled
                    />
                    :
                    <DatePicker id={"survey-start"}
                                selectsStart startDate={startDate} endDate={endDate}
                                showTimeSelect timeIntervals={15} timeFormat={timeFormat} timeCaption={timeCaption}
                                selected={startDate}
                                dateFormat={dateFormat} locale={locale}
                                placeholderText={datepickerPlaceholder}
                                showMonthYearDropdown dateFormatCalendar={"MMM yyyy"} minDate={minDate} maxDate={maxDate} todayButton={intl.formatMessage({defaultMessage:'Today',id:'today'})}
                                onChange={date => {
                                    const newSurveyData = {...surveyData}
                                    newSurveyData.starttime = dateToServerFormatWithZeroSeconds(date)
                                    setSurveyData(newSurveyData)
                                }}
                    />
                }
            </div>
            <div>
                <label htmlFor={"survey-end"}>
                    {intl.formatMessage(
                        {
                            id: 'survey.information.endTime',
                            defaultMessage: 'End Time',
                        })}
                </label>
                {surveyFinished ?
                    <DatePicker id={"survey-end"}
                                selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
                                showTimeSelect timeIntervals={15} timeFormat={timeFormat} timeCaption={timeCaption}
                                selected={endDate}
                                dateFormat={dateFormat} locale={locale}
                                placeholderText={datepickerPlaceholder}
                                showMonthYearDropdown dateFormatCalendar={"MMM yyyy"} minDate={minDate} maxDate={maxDate} todayButton={intl.formatMessage({defaultMessage:'Today',id:'today'})}
                                readOnly disabled
                    />
                    :
                    <DatePicker id={"survey-end"}
                                selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
                                showTimeSelect timeIntervals={15} timeFormat={timeFormat} timeCaption={timeCaption}
                                selected={endDate}
                                dateFormat={dateFormat} locale={locale}
                                placeholderText={datepickerPlaceholder}
                                showMonthYearDropdown dateFormatCalendar={"MMM yyyy"} minDate={minDate} maxDate={maxDate} todayButton={intl.formatMessage({defaultMessage:'Today',id:'today'})}
                                onChange={date => {
                                    const newSurveyData = {...surveyData}
                                    newSurveyData.endtime = dateToServerFormatWithZeroSeconds(date)
                                    setSurveyData(newSurveyData)
                                }}
                    />
                }
            </div>
        </div>
        {instanceHasMultipleLanguageOptions &&
            <div className={"survey-dropdown"}>
                <label htmlFor={"survey-language"}>
                    {intl.formatMessage(
                        {
                            id: 'language',
                            defaultMessage: 'Language',
                        })}
                </label>
                <select id={"survey-language"} value={surveyLanguage} onChange={(e) => handleSurveyDataChange("language", e)}>
                    {instanceLangOptions.map(lang=>{
                        return <option value={lang} key={lang}>
                            {intl.formatMessage(
                                {
                                    id: lang,
                                    defaultMessage: lang,
                                })}
                        </option>
                    })}
                </select>
            </div>
        }
        <div className={"sub-group"}>
            <SurveyCoordinators />
        </div>
        <PopupElement />
    </PopupProvider>
}

function SurveyReminders() {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED"
    const intl = useIntl()
    let reminders
    if (surveyData.config.hasOwnProperty('reminders')) {
        reminders = surveyData.config["reminders"]
    } else {
        reminders = []
    }
    const addReminder = () => {
        const reminderDelay = reminders?.length < 1 ? 24 : parseInt(reminders[reminders?.length - 1]?.delay) + parseInt(reminders[0]?.delay)
        let newReminder = {
            delay: reminderDelay,
            message: ""
        }
        reminders.push(newReminder)
        const newSurveyData = {...surveyData}
        newSurveyData.config["reminders"] = reminders
        setSurveyData(newSurveyData)
    }

    return <>
        <div className={"reminders"}>
            <h4>
                {intl.formatMessage(
                    {
                        id: 'survey.navigation.reminders',
                        defaultMessage: 'Reminders',
                    })}
            </h4>
            {reminders.length < 1 &&
            <p className={"placeholder"}>
                {intl.formatMessage(
                    {
                        id: 'survey.reminderMissing.placeholder',
                        defaultMessage: 'Your survey doesn\'t have any reminders. Start adding by clicking the button below.',
                    })}
            </p>
            }
            {reminders.map((reminder, i) => {
                return <Reminder reminder={reminder} i={i} key={i}/>
            })}
            {surveyFinished ?
                <button className={"add-new-reminder add-new-button"} disabled>
                    {intl.formatMessage(
                        {
                            id: 'survey.reminders.addReminder',
                            defaultMessage: '+ Add reminder',
                        })}
                </button>
                :
                <button className={"add-new-reminder add-new-button"} onClick={addReminder}>
                    {intl.formatMessage(
                        {
                            id: 'survey.reminders.addReminder',
                            defaultMessage: '+ Add reminder',
                        })}
                </button>
            }
        </div>
    </>
}

function Reminder({ reminder, i }) {
    const [editorIsActive, setEditorIsActive] = useState(false)
    const editorContainer = useRef(null)

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside)
        function handleClickOutside(event) {
            if (editorContainer.current && !editorContainer.current.contains(event.target) ) {
                setEditorIsActive(false)
            }
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        }
    },[editorContainer])

    const handleEditorClick = () => {
        setEditorIsActive(true);
    }

    return <div className={"message reminder"}>
        <div className={"message-icon"}>
            <ReminderIcon />
        </div>
        <div className={"message-content"}>
            <div>
                <div ref={editorContainer} onClick={handleEditorClick}>
                    <EditableContent text={reminder.message} i={i} editorIsActive={editorIsActive} />
                </div>
                <ReminderPopup i={i} />
            </div>
            <ReminderTools i={i} />
        </div>
    </div>
}

function ReminderTools({i}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const reminderTime = surveyData.config["reminders"][i].delay
    const intl = useIntl()

    const handleInputChange = e => {
        const newSurveyData = {...surveyData}
        let value = e.target.value;

        if (value === '') {
            newSurveyData.config["reminders"][i].delay = value
        } else {
            value = Number(value)
            if (!isNaN(value)) {
                newSurveyData.config["reminders"][i].delay = value
            }
        }
        setSurveyData(newSurveyData)
    };

    const handleInputBlur = () => {
        if (reminderTime === '' || isNaN(reminderTime)) {
            const newSurveyData = {...surveyData}
            newSurveyData.config["reminders"][i].delay = 24 // Default value
            setSurveyData(newSurveyData)
        }
    };

    return <div className={"reminder-tools"}>
        <ReminderIconSmall />
        {surveyFinished ?
            <input type={"number"} value={reminderTime}
                   disabled/> :
            <input
                type={"number"}
                value={reminderTime === 0 ? '' : reminderTime}
                placeholder="24"
                onChange={handleInputChange}
                onBlur={handleInputBlur}
            />
        }
         {intl.formatMessage(
        {
            id: 'survey.reminders.hours',
            defaultMessage: 'hours',
        })}
    </div>
}

function ReminderPopup({i}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"
    const [popoverOpen, setPopoverOpen] = useState(false)
    const intl = useIntl()
    const deleteReminder = () => {
        const newSurveyData = {...surveyData}
        newSurveyData.config["reminders"].splice(i, 1)
        setSurveyData(newSurveyData)
        setPopoverOpen(false)
    }
    if (surveyFinished) {
        return null
    }
    return <div className={"options-popover popover-container"}>
        <div className={"popover-toggle"} onClick={() => setPopoverOpen(true)}>
            <OptionsIcon />
        </div>
        {popoverOpen &&
        <Popover closePopover={() => setPopoverOpen(false)}>
            <button className={"delete"} onClick={deleteReminder}>
                <DeleteIcon />
                {intl.formatMessage(
                    {
                        id: 'survey.message.deleteReminder',
                        defaultMessage: 'Delete Reminder',
                    })}
            </button>
        </Popover>
        }
    </div>
}

function EditableContent(props) {
    const text = props?.text
    const editorIsActive = props?.editorIsActive
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const { setFocus } = useContext(EditorFocusContext)
    const surveyEditable = surveyData.status === "DRAFT" || surveyData.status === "SCHEDULED"
    const [editorState, setEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromText(props.text)),
    )
    const intl = useIntl()

    //Set variable component and update editor state with it onload
    const decorators = new CompositeDecorator([
        {
            strategy: variableStrategy,
            component: VariableComponent,
            editable: false,
            props: {
                editorState: editorState,
                editorUpdate: e => editorUpdate(e)
            }
        }])
    useEffect(()=>{
        setEditorState(EditorState.set(editorState, {decorator: decorators}))
    },[])

    // Update editor state if content changed elsewhere eg. undo
    useEffect(()=> {
        if (editorState.getCurrentContent().getPlainText() !== text && !editorIsActive) {
            const newContent = ContentState.createFromText(text)
            const newState = EditorState.push(editorState, newContent, 'insert-characters')
            const stateWithDecorators = EditorState.set(newState, {decorator: decorators})
            // Clear editors undo stack
            const clearedHistoryState = EditorState.set(stateWithDecorators, {
                undoStack: EditorState.createEmpty().getUndoStack()
            })

            setEditorState(clearedHistoryState)
        }
    },[text])

    // Instead of debounce, just save every 5 seconds to global state to not overflow it with every update
    useEffect(() => {
        const saveInterval = setInterval(() => {
            if (editorIsActive) saveToContext()
        }, 5000)

        // Clean up timer
        return () => {
            clearInterval(saveInterval)
        }
    }, [editorState])

    const saveToContext = () => {
        const plainText = editorState.getCurrentContent().getPlainText()
        const newSurveyData = {...surveyData}

        newSurveyData.config["reminders"][props.i].message = plainText

        setSurveyData(newSurveyData)
    }

    const editorUpdate = e => {
        // update editor with variable decorator
        const editorStateWithDecorators = EditorState.set(e, {decorator: decorators})
        setEditorState(editorStateWithDecorators)
    }

    const handleBlur = () => {
        setFocus('reminder'+props?.i, false)
        saveToContext()
    }

    const pasteVariable = variable => {
        const newState = pasteText(variable, editorState)
        editorUpdate(newState)
    }

    const pasteText = (text, editorValue) => {
        const selection = editorValue.getSelection()
        const contentState = editorValue.getCurrentContent()
        const newContent = Modifier.insertText(contentState, selection, text)
        const newEditorState = EditorState.push(editorValue, newContent, 'insert-characters')
        return EditorState.forceSelection(newEditorState, newContent.getSelectionAfter())
    }

    const handleUndo = () => {
        const undoStack = editorState.getUndoStack()

        if (undoStack.size !== 0) {
            const newEditorState = EditorState.undo(editorState)
            setEditorState(newEditorState)
        }
    }

    const handleKeyCommand = (command) => {
        if (command === 'undo') {
            handleUndo()
            return 'handled'
        }

        // Handle other key commands or let Draft.js handle them
        return 'not-handled'
    }

    const placeholder = intl.formatMessage(
        {
            id: 'survey.reminder.placeholder',
            defaultMessage: 'Click to type your reminder',
        })

    return <div className={"editor"}>
        {surveyEditable ?
            <>
                <Editor
                    editorState={editorState}
                    onChange={editorUpdate}
                    placeholder={placeholder}
                    stripPastedStyles={true}
                    onFocus={() => setFocus('reminder'+props?.i, true)}
                    onBlur={handleBlur}
                    handleKeyCommand={handleKeyCommand}
                />
                {props.editorIsActive &&
                    <div className={"editor-toolbar"}>
                        <AddVariable pasteVariable={variable => pasteVariable(variable)}/>
                        <LengthIndicator content={editorState.getCurrentContent().getPlainText()}/>
                    </div>
                }
            </> :
            <Editor editorState={editorState} readOnly={true} disabled stripPastedStyles={true} placeholder={placeholder} />
        }
    </div>
}

function SurveySkeleton() {
    return <div className={"survey-container skeleton-container"}>
        <div className={"survey-header"}>
            <Skeleton width={17} height={17} circle={true} noMargin={true} classText={"back"}/>
            <Skeleton width={250} height={24} noMargin={true}/>
            <Skeleton width={57} height={17} noMargin={true} classText={"status"}/>
            <div className={"save-toolbar"}>
                <Skeleton width={40} height={17} noMargin={true} classText={"cancel"}/>
                <Skeleton width={150} height={40} noMargin={true} classText={"draft"}/>
                <Skeleton width={90} height={40} noMargin={true} classText={"save"}/>
            </div>
        </div>
        <div className={"survey-view"}>
            <div className={"survey-content"}>
                <div className={"survey-navigation"}>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                    <Skeleton width={80} height={20} noMargin={true}/>
                </div>
                <div className={"survey-edit"}>
                    <Skeleton width={40} height={20}/>
                    <Skeleton height={55} noMargin={true} classText={"input"}/>
                    <div className={"survey-time"}>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                        <div>
                            <Skeleton width={40} height={20}/>
                            <Skeleton height={55} noMargin={true} classText={"input"}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
}